import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { GreenroomState as GS } from '../../../../stores/types/types';
import {
	OpenButton,
	StyledButton,
	StyledLabel,
	StyledLi,
	StyledSpan,
	StyledUl,
	HeaderContainer,
	ConfigurationUl,
	StyledSelect,
	StyledSmallButton,
	StyledInput,
} from '../debug-ui';
import Icon, { COLORS, ICONS } from '../../../general-components/icon';
import type { SrtStream } from '../../../../stores/types/video';
import { CreateSrtStream, DeleteSrtStream } from '../../../../connection/video';
import useDragItem from '../../../../hooks/use-dnd/use-drag-item';
import { DRAG_TYPE } from '../../../../stores/reducers/user-interface';
import useShots from '../../../../contexts/shots/provider';
import { FEED_TYPES } from '../../../../stores/reducers/shots';
import { IMG_TYPES } from '../../../../types/constants';
import { showAlert } from '../../../general-components/alert-service';
import { UploadImage, UploadResponse } from '../../../../connection/upload-image';
import { setMetaMenuState } from '../../../../stores/actions/tabs';
import { META_MENU_STATES } from '../../../../stores/types/tabs';
import { selectSrtStreams } from '../../../../stores/selectors/video';

const LIVE_STREAM_TYPES = {
	SRT: 'SRT',
	RTMP: 'RTMP',
};

interface SrtStreamItemProps {
	stream: SrtStream;
	deleteStream: (streamId: number) => Promise<boolean>;
}

function SrtStreamItem({ stream, deleteStream }: SrtStreamItemProps) {
	const dispatch = useDispatch();

	const metaMenuState = useSelector((s: GS) => s.TabsReducer.metaMenuState);

	const [open, setOpen] = useState<boolean>(true);
	const [isDeleting, setIsDeleting] = useState<boolean>(false);
	const [configurationType, setConfigurationType] = useState<string>(LIVE_STREAM_TYPES.SRT);

	const { dragRef, isDragging } = useDragItem(
		{ id: stream.id, type: DRAG_TYPE.FEED_SRT_STREAM },
		true,
	);

	const handleCopyUrl = () => {
		const url = configurationType === LIVE_STREAM_TYPES.SRT
			? `srt://global-live.mux.com:6001?streamid=${stream.streamKey}&passphrase=${stream.srtPassphrase}`
			: `rtmp://global-live.mux.com:5222/app/${stream.streamKey}`;
		navigator.clipboard.writeText(url);
	};

	const handleDeleteStream = async () => {
		setIsDeleting(true);
		const didDelete = await deleteStream(stream.id);
		if (!didDelete) {
			setIsDeleting(false);
		}
	};

	const configuration = useMemo(() => {
		if (configurationType === LIVE_STREAM_TYPES.SRT) {
			return {
				url: 'srt://global-live.mux.com:6001',
				stream_id: stream.streamKey,
				passphrase: stream.srtPassphrase,
				mode: 'caller',
				encryption_key_size: 128,
				pbkeylen: 16,
			};
		}
		return {
			url: 'rtmp://global-live.mux.com:5222/app',
			secure_url: 'rtmps://global-live.mux.com:443/app',
			stream_id: stream.streamKey,
		};
	}, [configurationType, stream]);

	// the meta menu being open prevents dnd
	useEffect(() => {
		if (isDragging && metaMenuState === META_MENU_STATES.OPEN) {
			dispatch(setMetaMenuState(META_MENU_STATES.CLOSING));
		}
	}, [isDragging, metaMenuState]);

	return (
		<StyledUl key={stream.id} ref={dragRef} className={classNames(isDragging && 'dragging')}>
			<OpenButton onClick={() => setOpen(!open)}>
				<Icon
					name={open ? ICONS.KEYBOARD_ARROW_DOWN : ICONS.KEYBOARD_ARROW_UP}
					size={14}
					color={COLORS.WHITE}
				/>
			</OpenButton>
			<StyledLi className="flex width-full padding-right-large">
				<StyledLabel>NAME:</StyledLabel>
				<StyledSpan className="truncate-overflow">{stream.name}</StyledSpan>
				<StyledLabel className="margin-left-medium">STATUS:</StyledLabel>
				<StyledSpan style={{ overflow: 'visible' }}>{stream.status}</StyledSpan>
			</StyledLi>
			{open ? (
				<>
					<div className="justify-content-space-between margin-top-small margin-bottom-small">
						<div className="flex-align-center">
							<StyledLabel>CONFIGURATION: </StyledLabel>
							<StyledSelect
								value={configurationType}
								onChange={(e) => setConfigurationType(e.target.value)}
								style={{ margin: 0 }}
							>
								{Object.keys(LIVE_STREAM_TYPES).map((res, index) => (
									<option key={index} value={res}>
										{res}
									</option>
								))}
							</StyledSelect>
						</div>
						<div className="flex-align-center gap-small">
							<StyledButton
								className="background-green text-dark"
								onClick={() => handleCopyUrl()}
							>
								Copy {configurationType === LIVE_STREAM_TYPES.SRT ? 'SRT' : 'RTMP'} string
							</StyledButton>
							<StyledButton
								className={classNames(`background-red text-white ${isDeleting && 'disabled'}`)}
								onClick={handleDeleteStream}
							>
								Delete
							</StyledButton>
						</div>
					</div>
					<StyledLi>
						<ConfigurationUl>
							{Object.entries(configuration).map(([key, value], index) => (
								<StyledLi key={index}>
									<StyledLabel>{key}:</StyledLabel>
									<StyledSpan>{value}</StyledSpan>
									<StyledSmallButton
										className="background-green text-dark"
										onClick={() => navigator.clipboard.writeText(value)}
									>
										Copy
									</StyledSmallButton>
								</StyledLi>
							))}
						</ConfigurationUl>
					</StyledLi>
				</>
			) : (
				<></>
			)}
		</StyledUl>
	);
}

export default function DebugSrtStreams() {
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const user = useSelector((s: GS) => s.SessionReducer.user);
	const { standaloneSrtStreams } = useSelector(selectSrtStreams);

	const [open, setOpen] = useState<boolean>(false);
	const [name, setName] = useState<string>('');
	const [safetyImage, setSafetyImage] = useState<string>('');
	const [uploadingImage, setUploadingImage] = useState<boolean>(false);

	const fileInput = useRef<HTMLInputElement | null>(null);

	const { stripFeedFromShots } = useShots();

	function safetyImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
		if (e.target.files) {
			setUploadingImage(true);
			UploadImage(token, e.target.files).then((res: UploadResponse) => {
				setUploadingImage(false);
				if (res.filename) {
					setSafetyImage(res.url);
				}
			}).catch((err) => {
				console.error(err);
				showAlert({
					message: 'Error uploading image',
					description: 'We ran into an issue uploading that image. Please try again.',
					duration: 4000,
				});
			}).finally(() => {
				if (fileInput.current) {
					fileInput.current.value = '';
				}
				setUploadingImage(false);
			});
		}
	}

	const handleCreateStream = async () => {
		if (!token || !meeting || !user) return;
		await CreateSrtStream(token, meeting.meeting, name, safetyImage);
		setName('');
		setSafetyImage('');
	};

	const deleteStream = async (streamId: number) => {
		if (!token || !meeting) return false;
		stripFeedFromShots(streamId, FEED_TYPES.SRT_STREAM);
		return !!await DeleteSrtStream(token, meeting.meeting, streamId);
	};

	return (
		<div className="section settings">
			<HeaderContainer>
				<h5>SRT Streams</h5>
			</HeaderContainer>
			<div className="advanced-settings-container">
				<StyledUl>
					<OpenButton onClick={() => setOpen(!open)}>
						<Icon
							name={open ? ICONS.KEYBOARD_ARROW_DOWN : ICONS.KEYBOARD_ARROW_UP}
							size={14}
							color={COLORS.WHITE}
						/>
					</OpenButton>
					{open ? (
						<>
							<StyledLi>
								<StyledLabel>NAME:</StyledLabel>
								<StyledInput
									type="text"
									value={name}
									placeholder="Stream Name"
									onChange={(e) => setName(e.target.value)}
								/>
							</StyledLi>
							<StyledLi className="justify-content-space-between margin-top-small margin-bottom-small">
								<StyledButton
									className="background-green text-dark"
									onClick={() => fileInput.current?.click()}
								>
									Upload Image
								</StyledButton>
								<StyledButton
									className="background-green text-dark"
									onClick={handleCreateStream}
									disabled={!name || !safetyImage || uploadingImage}
								>
									Create SRT Stream
								</StyledButton>
							</StyledLi>
							<input
								type="file"
								ref={fileInput}
								style={{ display: 'none' }}
								onChange={safetyImageUpload}
								accept={[...IMG_TYPES]
									.map((a) => `.${a}`)
									.join(',')
								}
							/>
						</>
					) : (
						<div className="flex-center">
							<h5>Create new SRT stream</h5>
						</div>
					)}
				</StyledUl>
			</div>
			<div className="advanced-settings-container">
					{standaloneSrtStreams.map((stream: SrtStream) => (
						<SrtStreamItem
							key={stream.id}
							stream={stream}
							deleteStream={deleteStream}
						/>
					))}
			</div>
		</div>
	);
}
